import { useState, useEffect, useContext } from 'react'
import { onProgress, isInitError, getError, INIT_PHASES } from 'Domain/Scene/Manager/InitializationOrchestrator'
import { ProjectContext } from 'Application/Context/ProjectContext'

import 'Application/Templates/Css/Components/Logo.css'


/**
 * Component Logo - Loading screen pilotee par l'InitializationOrchestrator
 * Affiche la progression des phases d'initialisation avec une barre de progression
 * En cas d'erreur, affiche le message d'erreur a la place de la barre
 * @returns {JSX}
 */
export const Logo = () => {

    const [progress, setProgress] = useState(0)
    const [label, setLabel] = useState('Starting project')
    const [visible, setVisible] = useState(true)
    const [error, setErrorState] = useState(null)
    const [fading, setFading] = useState(false)
    const [logoSrc, setLogoSrc] = useState(null)
    const [logoVariant, setLogoVariant] = useState(null)
    const [logoVisible, setLogoVisible] = useState(false)

    const project = useContext(ProjectContext)
    const caseLogoUrl = project?.caseLogoUrl || null
    const isValidationAction = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('action') === 'validate'

    useEffect(() => {
        if (isValidationAction) return

        let cancelled = false
        let revealTimer = null

        const showLogo = (src, variant) => {
            if (cancelled) return
            setLogoVisible(false)
            setLogoSrc(src)
            setLogoVariant(variant)
            revealTimer = setTimeout(() => {
                if (!cancelled) setLogoVisible(true)
            }, 0)
        }

        setLogoSrc(null)
        setLogoVariant(null)
        setLogoVisible(false)

        if (!project?.ref) {
            return () => {
                cancelled = true
            }
        }

        if (!caseLogoUrl) {
            showLogo('loadingLogo.png', 'default')
            return () => {
                cancelled = true
                if (revealTimer) clearTimeout(revealTimer)
            }
        }

        const preloader = new Image()
        preloader.onload = () => {
            showLogo(caseLogoUrl, 'company')
        }
        preloader.onerror = () => {
            showLogo('loadingLogo.png', 'default')
        }
        preloader.src = caseLogoUrl

        return () => {
            cancelled = true
            if (revealTimer) clearTimeout(revealTimer)
        }
    }, [project?.ref, caseLogoUrl, isValidationAction])

    useEffect(() => {
        if (isValidationAction) return

        const unsubscribe = onProgress((progressValue, currentPhase, currentLabel) => {
            if (currentPhase === INIT_PHASES.ERROR) {
                setErrorState(getError())
                return
            }

            if (currentPhase === INIT_PHASES.COMPLETE) {
                setFading(true)
                setTimeout(() => setVisible(false), 1200)
                return
            }

            setProgress(progressValue)
            setLabel(currentLabel)
        })

        return () => unsubscribe()
    }, [isValidationAction])

    const hasError = isInitError()
    const logoClass = `${logoVariant === 'company' ? 'logo-case' : ''} ${logoVisible || hasError ? 'logo-enter' : ''}`.trim()

    if (isValidationAction) return null

    if (!visible) return null

    return (
        <div id="Logo" className={fading ? 'fade-out' : ''}>
            <div className="logo-content">
                <div className="logo-slot">
                {logoSrc ? (
                    <img
                        key={logoSrc}
                        src={logoSrc}
                        alt="loading..."
                        className={logoClass}
                        style={{ opacity: logoVisible || hasError ? 1 : 0 }}
                    />
                ) : null}
                </div>
                <div className="logo-progress-container">
                    {hasError ? (
                        <p className="logo-error-message">{error}</p>
                    ) : (
                        <>
                            <div className="logo-progress-bar">
                                <div 
                                    className="logo-progress-fill" 
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <p className="logo-progress-label">{label}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
